import { useEffect, useState } from "react";
import { isBrokeredPreviewAuth, readStoredAuthSession } from "@/lib/auth-session";
import { supabase } from "@/integrations/supabase/client";

export type AuthUser = { id: string; email: string } | null;

/**
 * useAuthUser — the shared read-only view of "who is signed in".
 *
 * There is exactly ONE `supabase.auth.onAuthStateChange` subscription in the
 * app (AuthSync in src/routes/__root.tsx); it rebroadcasts every event as the
 * `rt-auth-change` window event. Components must read sign-in state through
 * this hook rather than opening their own subscription — parallel subscribers
 * contend on the auth LockManager and can stall session restoration,
 * especially on mobile Safari and inside the Capacitor app.
 *
 * Seeds synchronously from the stored session (after the first render, so the
 * SSR markup still matches) and never downgrades to "signed out" because a
 * lookup was slow: only a real auth event does that.
 */
export function useAuthUser(): AuthUser {
  const [user, setUser] = useState<AuthUser>(null);

  useEffect(() => {
    const stored = readStoredAuthSession();
    if (stored?.user?.id) {
      setUser({ id: stored.user.id, email: stored.user.email ?? "" });
    }
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail as
        | { event?: string; session?: { user?: { id?: string; email?: string } } | null }
        | undefined;
      const evt = detail?.event;
      if (
        evt &&
        evt !== "SIGNED_IN" &&
        evt !== "SIGNED_OUT" &&
        evt !== "USER_UPDATED" &&
        evt !== "INITIAL_SESSION" &&
        evt !== "TOKEN_REFRESHED"
      ) {
        return;
      }
      const u = detail?.session?.user;
      setUser(u?.id ? { id: u.id, email: u.email ?? "" } : null);
    };
    window.addEventListener("rt-auth-change", handler);

    // Inside the Lovable editor preview the session is read from the editor
    // over postMessage; that read can time out and briefly look signed out,
    // which made the header offer "Join" to a signed-in admin. Re-ask a
    // couple of times there before trusting the empty answer.
    const timers: number[] = [];
    let alive = true;
    if (!stored?.user?.id && isBrokeredPreviewAuth()) {
      const recheck = (attempt: number) => {
        timers.push(
          window.setTimeout(() => {
            void supabase.auth.getSession().then(({ data }) => {
              if (!alive) return;
              const u = data?.session?.user;
              if (u?.id) setUser({ id: u.id, email: u.email ?? "" });
              else if (attempt < 1) recheck(attempt + 1);
            });
          }, 1_200),
        );
      };
      recheck(0);
    }

    return () => {
      alive = false;
      timers.forEach((t) => window.clearTimeout(t));
      window.removeEventListener("rt-auth-change", handler);
    };
  }, []);

  return user;
}

/** Convenience wrapper for components that only need a boolean. */
export function useIsSignedIn(): boolean {
  return useAuthUser() !== null;
}
